import { useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Message,
  Popconfirm,
  Alert,
} from '@arco-design/web-react'
import { IconPlus, IconRefresh, IconEdit, IconDelete, IconLink } from '@arco-design/web-react/icon'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import {
  getDeploymentsApi,
  createDeploymentApi,
  updateDeploymentApi,
  deleteDeploymentApi,
  getDeploymentSettingsApi,
  testDeploymentConnectionApi,
  type ModelDeployment,
} from '@/api/deployment'
import { renderTimestamp, Pill } from '@/utils/render'
import { useRole } from '@/hooks/useRole'
import PageHeader from '@/components/Layout/PageHeader'
import { getFormValidateErrorMessage } from '@/utils/helpers'

export default function ModelDeploymentPage() {
  const navigate = useNavigate()
  const { isRoot } = useRole()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<ModelDeployment | null>(null)
  const [form] = Form.useForm()

  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ['deployments', { page, pageSize }],
    queryFn: () => getDeploymentsApi({ p: page, page_size: pageSize }),
    retry: 1,
  })
  const settings = useQuery({
    queryKey: ['deployment-settings'],
    queryFn: getDeploymentSettingsApi,
  })

  const testConn = useMutation({
    mutationFn: () => testDeploymentConnectionApi(),
    onSuccess: () => {
      Message.success('连接成功')
    },
    onError: (e: Error) => Message.error(e.message || '连接失败'),
  })

  const createMut = useMutation({
    mutationFn: createDeploymentApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deployments'] }),
  })
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ModelDeployment> }) => updateDeploymentApi(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deployments'] }),
  })
  const deleteMut = useMutation({
    mutationFn: deleteDeploymentApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deployments'] }),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    setModalOpen(true)
  }

  const openEdit = (row: ModelDeployment) => {
    setEditing(row)
    form.setFieldsValue(row)
    setModalOpen(true)
  }

  const handleSubmit = async () => {
    try {
      const v = (await form.validate()) as Partial<ModelDeployment>
      if (editing) {
        await updateMut.mutateAsync({ id: editing.id, data: { ...editing, ...v } })
      } else {
        await createMut.mutateAsync(v)
      }
      Message.success('已保存')
      setModalOpen(false)
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    }
  }

  const s = settings.data
  const ioReady = s?.enabled === true && s?.configured === true

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '名称', dataIndex: 'name', width: 200 },
    { title: '模型', dataIndex: 'model', width: 180 },
    { title: '供应商', dataIndex: 'provider', width: 140 },
    { title: 'Base URL', dataIndex: 'base_url', width: 240 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: number) => (v === 1 ? <Pill variant="success">已启用</Pill> : <Pill variant="neutral">已禁用</Pill>),
    },
    { title: '创建时间', dataIndex: 'created_time', width: 170, render: renderTimestamp },
    {
      title: '操作',
      fixed: 'right' as const,
      width: 160,
      render: (_: unknown, row: ModelDeployment) => (
        <Space size={2}>
          <Button size="small" type="text" icon={<IconEdit />} onClick={() => openEdit(row)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除？"
            onOk={() => deleteMut.mutateAsync(row.id).then(() => Message.success('已删除'))}
          >
            <Button size="small" type="text" status="danger" icon={<IconDelete />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="模型部署"
        subtitle="依赖 io.net 企业 API；开关与密钥请在「系统设置 → 部署」中由 Root 配置"
        actions={
          isRoot ? (
            <Button type="outline" icon={<IconLink />} onClick={() => navigate('/console/setting?tab=model-deployment')}>
              打开系统设置
            </Button>
          ) : null
        }
      />

      <Card className="!rounded-2xl" bordered={false}>
        {settings.isLoading ? (
          <div className="text-sm text-[#6B7280]">加载部署服务状态…</div>
        ) : (
          <div className="space-y-3">
            <Alert
              type={ioReady && s?.can_connect ? 'success' : 'warning'}
              content={
                <div className="text-sm">
                  <div>
                    提供商：{s?.provider || 'io.net'}，功能开关：{s?.enabled ? '已开启' : '未开启'}
                    ，已配置密钥：{s?.configured ? '是' : '否'}
                    ，可连接：{s?.can_connect ? '是' : '否'}
                  </div>
                  {!s?.enabled && isRoot && (
                    <div className="mt-2">
                      请前往
                      <Link to="/console/setting?tab=model-deployment" className="text-brand font-medium mx-1">
                        系统设置 → 部署
                      </Link>
                      启用「model_deployment.ionet」并填写 API Key。
                    </div>
                  )}
                </div>
              }
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="primary"
                loading={testConn.isPending}
                disabled={!s?.enabled || !s?.configured}
                onClick={() => testConn.mutate()}
              >
                测试连接
              </Button>
              <span className="text-xs text-[#6B7280]">
                使用已保存在选项中的 API Key 调用上游校验（POST /api/deployments/test-connection）。
              </span>
            </div>
          </div>
        )}
      </Card>

      <Card
        className="!rounded-2xl"
        title="部署列表"
        bordered={false}
        extra={
          <Space>
            <Button icon={<IconRefresh />} onClick={() => list.refetch()}>
              刷新
            </Button>
            <Button
              type="primary"
              icon={<IconPlus />}
              onClick={openCreate}
              disabled={!ioReady}
            >
              新建部署
            </Button>
          </Space>
        }
      >
        {!ioReady && (
          <Alert
            className="mb-4"
            type="info"
            content="IO 未就绪时创建/管理部署可能被后端拒绝。请先完成系统设置中的 io.net 配置。"
          />
        )}
        {list.isError && (
          <Alert
            className="mb-4"
            type="error"
            content={list.error instanceof Error ? list.error.message : '加载失败'}
          />
        )}
        <Table
          rowKey="id"
          loading={list.isLoading}
          columns={columns}
          data={list.data?.items || []}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total: list.data?.total || 0,
            showTotal: true,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
        />
      </Card>

      <Modal
        title={editing ? '编辑部署' : '新建部署'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={createMut.isPending || updateMut.isPending}
        style={{ width: 520 }}
      >
        <Form form={form} layout="vertical">
          <Form.Item field="name" label="名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item field="model" label="模型" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item field="provider" label="供应商">
            <Input />
          </Form.Item>
          <Form.Item field="base_url" label="Base URL">
            <Input />
          </Form.Item>
          <Form.Item field="api_key" label="API Key">
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
